import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';

import { SIDEBAR_MENU_ICONS } from '@/components/AppLayout/consts';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SIDEBAR_FLYOUT_CLASS_NAME,
  SIDEBAR_FLYOUT_ITEM_CLASS_NAME,
  SIDEBAR_FLYOUT_LABEL_CLASS_NAME,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
} from '@/components/ui/sidebar';

import { NavItem, NavItemWithSubItems } from '@/types';

import { useSidebarContext } from '@/providers';

type SidebarNavItemProps = {
  item: NavItem | NavItemWithSubItems;
};

export const SidebarNavItem = ({ item }: SidebarNavItemProps) => {
  const { t } = useTranslation('header');
  const { state: sidebarState } = useSidebarContext();
  const IconComponent =
    SIDEBAR_MENU_ICONS[item.icon as keyof typeof SIDEBAR_MENU_ICONS] ||
    SIDEBAR_MENU_ICONS['Default'];

  const isSubmenu = 'submenu' in item;
  const initialOpen = isSubmenu
    ? item.submenu.some((submenuItem) => submenuItem.active)
    : false;
  const [submenuOpen, setSubmenuOpen] = useState(initialOpen);

  const onSubmenuOpenChange = (next: boolean) => {
    setSubmenuOpen(next);
  };

  if (isSubmenu) {
    const isActive = item.submenu.some((submenuItem) => submenuItem.active);

    return (
      <Fragment>
        <Collapsible
          asChild
          open={submenuOpen}
          onOpenChange={onSubmenuOpenChange}
          className="group/collapsible"
        >
          <SidebarMenuItem>
            {sidebarState === 'collapsed' ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    tooltip={t(item.text)}
                    isActive={isActive}
                  >
                    <IconComponent className="shrink-0" />
                    <span className="whitespace-nowrap">{t(item.text)}</span>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="right"
                  align="start"
                  sideOffset={20}
                  className={SIDEBAR_FLYOUT_CLASS_NAME}
                >
                  <div className={SIDEBAR_FLYOUT_LABEL_CLASS_NAME}>
                    {t(item.text)}
                  </div>
                  {item.submenu.map((submenuItem) => (
                    <DropdownMenuItem
                      asChild
                      key={submenuItem.href + submenuItem.text}
                      data-active={submenuItem.active}
                      className={SIDEBAR_FLYOUT_ITEM_CLASS_NAME}
                    >
                      <a
                        href={submenuItem.href}
                        {...(submenuItem.active
                          ? { 'data-nav-active': true }
                          : {})}
                      >
                        {t(submenuItem.text)}
                      </a>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <CollapsibleTrigger asChild>
                <SidebarMenuButton tooltip={t(item.text)}>
                  <IconComponent className="shrink-0" />
                  <span className="whitespace-nowrap">{t(item.text)}</span>
                  <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                </SidebarMenuButton>
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down animation-duration-150 grid grid-rows-[1fr] overflow-hidden transition-[grid-template-rows] duration-150! ease-linear group-data-[collapsible=icon]:grid-rows-[0fr]">
              <div className="min-h-0 overflow-hidden">
                <SidebarMenuSub className="mx-0 mt-1 gap-1 border-none p-0 group-data-[collapsible=icon]:flex">
                  {item.submenu.map((submenuItem) => (
                    <SidebarMenuSubItem
                      key={submenuItem.href + submenuItem.text}
                    >
                      <SidebarMenuSubButton
                        asChild
                        isActive={submenuItem.active}
                      >
                        <a
                          href={submenuItem.href}
                          className="min-w-0"
                          {...(submenuItem.active
                            ? { 'data-nav-active': true }
                            : {})}
                        >
                          <span className="line-clamp-3 w-[calc(var(--sidebar-width)-63px)] shrink-0 whitespace-normal">
                            {t(submenuItem.text)}
                          </span>
                        </a>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </div>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
        {item.separator && <SidebarSeparator className="mx-0 my-0.5" />}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          tooltip={t(item.text)}
          isActive={item.active}
        >
          <a
            href={item.href}
            className="flex min-w-0 items-center gap-2"
          >
            <IconComponent className="shrink-0" />
            <span className="truncate">{t(item.text)}</span>
          </a>
        </SidebarMenuButton>
      </SidebarMenuItem>
      {item.separator && <SidebarSeparator className="mx-0 my-0.5" />}
    </Fragment>
  );
};
